import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, Truck, MapPin, FileText, Shield, MessageSquare, 
  BarChart3, Grid, CreditCard, LogOut, Menu, X,
  Crown, AlertCircle, RefreshCw, AlertTriangle,
  ChevronRight, Check, Calendar, Package, DollarSign, Clock,
  Download, Eye, Send, Filter, Search, Bell, Users, 
  Activity, Settings, ArrowLeft, XCircle, CheckCircle, Edit, Layers,
  Loader2, ArrowRight, Play, GraduationCap, FolderOpen
} from 'lucide-react';
import axios from 'axios';
import SEO from '../../components/shared/SEO';
import AdminDetailPanel, { InteractiveStatCard } from '../../components/admin/AdminDetailPanel';
import BuildProgressModule from '../../components/hive/BuildProgressModule';
import ProtectCommandCenter from '../../components/hive/ProtectCommandCenter';
import GrowCommandCenter from '../../components/hive/GrowCommandCenter';
import AuthorityStatusWidget from '../../components/hive/AuthorityStatusWidget';
import AcademyModule from '../../components/hive/AcademyModule';
import ResourcesModule from '../../components/hive/ResourcesModule';
import CarrierMessages from '../../components/hive/CarrierMessages';
import DocumentSigningFlow from '../../components/hive/DocumentSigningFlow';
import OnboardingProgressMap from '../../components/hive/OnboardingProgressMap';
import WelcomeConcierge from '../../components/hive/WelcomeConcierge';
import MilestoneCelebration from '../../components/hive/MilestoneCelebration';
import ReferralWidget from '../../components/hive/ReferralWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLORS = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#D4AF37',
  admin: '#D4AF37'
};

const TIER_NAMES = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  admin: 'Admin'
};

// ============ USER TYPE CONSTANTS ============
const USER_TYPES = {
  BUILD: 'build',      // Starter pack clients (Build phase)
  OPERATOR: 'operator', // Active operators (never bought Build)
  ADMIN: 'admin',      // Admin users
  ACADEMY: 'academy'   // Academy-only students (no active subscriptions)
};

// Signature Program Tiers with included support
const STARTER_PACK_TIERS = {
  basic: { name: 'Foundation', price: 997, launchSupportDays: 30, complianceDays: 30, dispatchDays: 30 },
  foundation: { name: 'Foundation', price: 997, launchSupportDays: 30, complianceDays: 30, dispatchDays: 30 },
  professional: { name: 'Accelerator', price: 1997, launchSupportDays: 60, complianceDays: 60, dispatchDays: 60 },
  accelerator: { name: 'Accelerator', price: 1997, launchSupportDays: 60, complianceDays: 60, dispatchDays: 60 },
  elite: { name: 'Elite', price: 2997, launchSupportDays: 90, complianceDays: 90, dispatchDays: 90 }
};

// ============ CORE LOGIC FUNCTIONS ============

/**
 * Determine user type based on their data
 * Priority: Admin > Build Client (has starter pack) > Academy Student > Operator
 */
const getUserType = (user, lifecycleData) => {
  if (!user) return null;
  
  // Admin check first
  if (user.role === 'admin' || user.dispatch_tier === 'admin') {
    return USER_TYPES.ADMIN;
  }
  
  // Check if user is a Build client (has/had starter pack)
  // Use lifecycle data structure from API
  const isBuildClient = lifecycleData?.user_type?.is_build_client || 
                        lifecycleData?.build?.show_tracker ||
                        user?.has_starter_pack || 
                        user?.subscription_status === 'starter_pack';
  
  if (isBuildClient) {
    return USER_TYPES.BUILD;
  }
  
  // Check if Academy-only student (has academy access but no other active subscriptions)
  const hasAcademyAccess = user?.academy_access;
  const hasProtectSubscription = lifecycleData?.subscriptions?.protect?.status && 
                                 lifecycleData?.subscriptions?.protect?.status !== 'none';
  const hasGrowSubscription = lifecycleData?.subscriptions?.grow?.status && 
                              lifecycleData?.subscriptions?.grow?.status !== 'none';
  
  if (hasAcademyAccess && !hasProtectSubscription && !hasGrowSubscription) {
    return USER_TYPES.ACADEMY;
  }
  
  // Default to Operator (direct subscription without Build)
  return USER_TYPES.OPERATOR;
};

/**
 * Get starter pack tier info
 */
const getStarterPackTier = (user, lifecycleData) => {
  const tierKey = (lifecycleData?.build?.tier || user?.starter_pack_tier || 'foundation').toLowerCase();
  return STARTER_PACK_TIERS[tierKey] || STARTER_PACK_TIERS.foundation;
};

/**
 * Get lifecycle status for grace period and authority info
 */
const getLifecycleInfo = (user, lifecycleData) => {
  const authorityActive = lifecycleData?.authority?.status === 'active' || user?.authority_status === 'active';
  const gracePeriodEnd = lifecycleData?.grace_period?.end_date || user?.grace_period_end_date;
  
  // Subscription status - check nested structure from API
  const protectStatus = lifecycleData?.subscriptions?.protect?.status;
  const growStatus = lifecycleData?.subscriptions?.grow?.status;
  
  // "selected" means they chose a plan but may not have active billing yet
  // "active" means fully active subscription
  const hasProtect = protectStatus === 'active' || protectStatus === 'selected';
  const hasGrow = growStatus === 'active' || growStatus === 'selected';
  
  // Grace period calculation
  let gracePeriodDays = null;
  let gracePeriodExpired = false;
  let gracePeriodActive = lifecycleData?.grace_period?.active || false;
  
  if (lifecycleData?.grace_period?.days_remaining !== undefined) {
    gracePeriodDays = lifecycleData.grace_period.days_remaining;
    gracePeriodExpired = lifecycleData.grace_period.expired || false;
  } else if (gracePeriodEnd) {
    const endDate = new Date(gracePeriodEnd);
    const now = new Date();
    gracePeriodDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    gracePeriodExpired = gracePeriodDays <= 0;
  }
  
  return {
    authorityActive,
    hasProtect,
    hasGrow,
    hasSelectedPlan: hasProtect || hasGrow,
    gracePeriodDays: gracePeriodDays !== null ? Math.max(0, gracePeriodDays) : null,
    gracePeriodExpired,
    gracePeriodActive,
    buildComplete: authorityActive
  };
};

/**
 * Get navigation items based on user type
 * - Admin: All tabs including Finance
 * - Build Client: Dashboard, Build, Protect (preview), Grow (preview), Documents, Messages
 * - Academy Student: Dashboard, Academy, Documents, Messages
 * - Operator: Dashboard, Protect, Grow, Documents, Messages (NO Build tab)
 */
const getNavigationItems = (userType, lifecycle) => {
  // Base items everyone sees
  const baseItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home }
  ];
  
  // Admin sees everything
  if (userType === USER_TYPES.ADMIN) {
    return [
      ...baseItems,
      { id: 'build', label: 'Build', icon: Layers },
      { id: 'protect', label: 'Protect', icon: Shield },
      { id: 'grow', label: 'Grow', icon: Crown },
      { id: 'documents', label: 'Documents', icon: FileText },
      { id: 'messages', label: 'Messages', icon: MessageSquare },
      { id: 'academy', label: 'Academy', icon: GraduationCap },
      { id: 'finance', label: 'Finance', icon: DollarSign }
    ];
  }
  
  // Academy Student - sees Academy prominently
  if (userType === USER_TYPES.ACADEMY) {
    return [
      ...baseItems,
      { id: 'academy', label: 'Academy', icon: GraduationCap, badge: 'ENROLLED' },
      { id: 'documents', label: 'Documents', icon: FileText },
      { id: 'messages', label: 'Messages', icon: MessageSquare }
    ];
  }
  
  // Build Client - sees Build tab
  if (userType === USER_TYPES.BUILD) {
    return [
      ...baseItems,
      { id: 'build', label: 'Build', icon: Layers, badge: lifecycle?.buildComplete ? 'COMPLETE' : 'ACTIVE' },
      { id: 'protect', label: 'Protect', icon: Shield },
      { id: 'grow', label: 'Grow', icon: Crown },
      { id: 'documents', label: 'Documents', icon: FileText },
      { id: 'messages', label: 'Messages', icon: MessageSquare }
    ];
  }
  
  // Operator - NO Build tab
  if (userType === USER_TYPES.OPERATOR) {
    return [
      ...baseItems,
      { id: 'protect', label: 'Protect', icon: Shield },
      { id: 'grow', label: 'Grow', icon: Crown },
      { id: 'documents', label: 'Documents', icon: FileText },
      { id: 'messages', label: 'Messages', icon: MessageSquare }
    ];
  }
  
  return baseItems;
};

// ============ MAIN COMPONENT ============
const HiveDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [lifecycleData, setLifecycleData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Admin Mode State
  const [adminCommandData, setAdminCommandData] = useState(null);
  const [carrierList, setCarrierList] = useState([]);
  const [viewingAsCarrier, setViewingAsCarrier] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [carrierSearchQuery, setCarrierSearchQuery] = useState('');
  
  // Document Signing State
  const [documentsComplete, setDocumentsComplete] = useState(true); // Default true to avoid flash
  const [checkingDocuments, setCheckingDocuments] = useState(false);

  // Milestone + onboarding state
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [milestoneQueue, setMilestoneQueue] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('hive_token');
    const storedUser = localStorage.getItem('hive_user');
    
    if (!token || !storedUser) {
      navigate('/hive/login');
      return;
    }
    
    const userData = JSON.parse(storedUser);
    setUser(userData);
    
    // Check if admin
    const isAdminUser = userData.role === 'admin' || userData.dispatch_tier === 'admin';
    setIsAdmin(isAdminUser);
    
    if (isAdminUser) {
      fetchAdminCommandData(token);
      fetchCarrierList(token);
    } else {
      // Regular carrier flow - check subscription status
      // Valid statuses: active, trial, starter_pack (build clients), pending
      const validStatuses = ['active', 'trial', 'starter_pack', 'pending'];
      if (!userData.subscription_status || 
          (userData.subscription_status === 'canceled' && !userData.has_starter_pack)) {
        navigate('/hive/login');
        return;
      }
      
      if (userData.subscription_status === 'suspended') {
        navigate('/hive/subscribe');
        return;
      }
      
      fetchCarrierDashboard(token, userData);
    }
  }, [navigate]);

  const fetchAdminCommandData = async (token) => {
    try {
      const res = await axios.get(`${API}/hive/admin/command-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminCommandData(res.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      if (err.response?.status === 403) {
        setIsAdmin(false);
        const userData = JSON.parse(localStorage.getItem('hive_user'));
        fetchCarrierDashboard(token, userData);
        return;
      }
      setError('Failed to fetch admin dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCarrierList = async (token) => {
    try {
      const res = await axios.get(`${API}/hive/admin/carrier-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarrierList(res.data);
    } catch (err) {
      console.error('Failed to fetch carrier list:', err);
    }
  };

  const fetchCarrierDashboard = async (token, userData) => {
    try {
      // Fetch dashboard data, lifecycle status, and document status in parallel
      const [dashboardRes, lifecycleRes, docStatusRes] = await Promise.all([
        axios.get(`${API}/hive/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/hive/me/lifecycle-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${API}/documents/user/${userData.id || userData._id}/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { all_signed: true } })) // Default to true if endpoint fails
      ]);
      
      // Check if documents are complete
      setDocumentsComplete(docStatusRes.data?.all_signed ?? true);
      
      setDashboardData(dashboardRes.data);
      setLifecycleData(lifecycleRes.data);
      
      // Update user with merged data
      const updatedUser = {
        ...userData,
        dispatch_tier: dashboardRes.data.dispatch_tier,
        has_starter_pack: lifecycleRes.data?.is_build_client || dashboardRes.data?.has_starter_pack,
        authority_status: lifecycleRes.data?.authority_status || dashboardRes.data?.authority_status,
        starter_pack_tier: lifecycleRes.data?.starter_pack_tier || dashboardRes.data?.starter_pack_tier
      };
      localStorage.setItem('hive_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setError(null);
      
      // Check if this is primarily an Academy student (has academy access but no active subscriptions)
      const lifecycle = lifecycleRes.data;
      const hasAcademyAccess = userData.academy_access || dashboardRes.data?.academy_access;
      const hasBuildSubscription = lifecycle?.is_build_client || lifecycle?.build?.has_started;
      const hasProtectSubscription = lifecycle?.subscriptions?.protect?.status && lifecycle?.subscriptions?.protect?.status !== 'none';
      const hasGrowSubscription = lifecycle?.subscriptions?.grow?.status && lifecycle?.subscriptions?.grow?.status !== 'none';
      
      // If user has academy access but no other active subscriptions, show Academy first
      const isAcademyOnlyUser = hasAcademyAccess && !hasBuildSubscription && !hasProtectSubscription && !hasGrowSubscription;
      
      if (isAcademyOnlyUser) {
        setActiveSection('academy');
      }

      // Fetch unseen milestones (non-blocking)
      const uid = userData.user_id || userData.id || userData._id;
      if (uid) {
        axios.get(`${API}/hive/milestones/unseen/${uid}`)
          .then((res) => {
            if (Array.isArray(res.data) && res.data.length > 0) {
              setMilestoneQueue(res.data);
              setCurrentMilestone(res.data[0]);
            }
          })
          .catch(() => {});
      }
    } catch (err) {
      setError('Failed to fetch data');
      if (err.response?.status === 403) {
        navigate('/hive/subscribe');
      } else if (err.response?.status === 401) {
        localStorage.removeItem('hive_token');
        localStorage.removeItem('hive_user');
        navigate('/hive/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewAsCarrier = async (carrierId) => {
    const token = localStorage.getItem('hive_token');
    try {
      const res = await axios.get(`${API}/hive/admin/carrier/${carrierId}/view-as`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewingAsCarrier(carrierId);
      setImpersonatedUser(res.data.user);
      setDashboardData(res.data);
      setLifecycleData(res.data.lifecycle || {});
      setActiveSection('dashboard');
    } catch (err) {
      console.error('Failed to load carrier view:', err);
      alert('Failed to load carrier data');
    }
  };

  const handleExitImpersonation = () => {
    setViewingAsCarrier(null);
    setImpersonatedUser(null);
    setDashboardData(null);
    setLifecycleData(null);
    setActiveSection('dashboard');
  };

  const handleLogout = () => {
    // Clear ALL auth tokens
    localStorage.removeItem('hive_token');
    localStorage.removeItem('hive_user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/hive/login');
  };

  const fetchSubscriptionData = async () => {
    const token = localStorage.getItem('hive_token');
    if (!token || isAdmin) return;
    
    setSubscriptionLoading(true);
    try {
      const res = await axios.get(`${API}/hive/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionData(res.data);
    } catch (err) {
      console.error('Failed to fetch subscription data:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const openPlanSelection = () => {
    setShowSubscriptionModal(true);
    fetchSubscriptionData();
  };

  // Filter carriers by search query
  const filteredCarriers = carrierList.filter(c => {
    const query = carrierSearchQuery.toLowerCase();
    return (
      c.company_name?.toLowerCase().includes(query) ||
      c.email?.toLowerCase().includes(query) ||
      c.contact_name?.toLowerCase().includes(query) ||
      c.dot_mc_number?.toLowerCase().includes(query)
    );
  });

  // Determine display user and derived values
  const displayUser = viewingAsCarrier ? impersonatedUser : user;
  const dispatch_tier = viewingAsCarrier 
    ? impersonatedUser?.dispatch_tier || 'bronze'
    : (dashboardData?.dispatch_tier || user?.dispatch_tier || 'bronze');
  const tierColor = TIER_COLORS[dispatch_tier] || TIER_COLORS.bronze;
  const tierName = TIER_NAMES[dispatch_tier] || 'Bronze';
  
  // Core logic calculations
  const userType = getUserType(displayUser, lifecycleData);
  const lifecycle = getLifecycleInfo(displayUser, lifecycleData);
  const starterPackTier = getStarterPackTier(displayUser, lifecycleData);
  const navItems = getNavigationItems(userType, lifecycle);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Document Signing Flow (blocks dashboard until all documents signed)
  // Only applies to non-admin users
  if (!isAdmin && !documentsComplete && displayUser) {
    return (
      <DocumentSigningFlow
        userId={displayUser.id || displayUser._id}
        userEmail={displayUser.email}
        onComplete={() => setDocumentsComplete(true)}
      />
    );
  }

  // Admin Command Dashboard View (when not impersonating)
  if (isAdmin && !viewingAsCarrier) {
    return (
      <AdminCommandDashboard
        user={user}
        adminData={adminCommandData}
        carrierList={filteredCarriers}
        searchQuery={carrierSearchQuery}
        setSearchQuery={setCarrierSearchQuery}
        onViewAsCarrier={handleViewAsCarrier}
        onLogout={handleLogout}
        onRefresh={() => {
          const token = localStorage.getItem('hive_token');
          fetchAdminCommandData(token);
          fetchCarrierList(token);
        }}
      />
    );
  }

  // Render content based on active section
  // Get subscription tier info for Protect and Grow
  const getProtectTier = () => {
    const status = lifecycleData?.subscriptions?.protect?.status || displayUser?.protect_subscription_status || 'none';
    // Status could be 'enterprise', 'professional', 'basic', 'active', 'selected', or 'none'
    if (status === 'enterprise') return 'enterprise';
    if (status === 'professional') return 'professional';
    if (status === 'basic' || status === 'active' || status === 'selected') return 'basic';
    if (status !== 'none' && status !== 'locked') return 'basic'; // Default active to basic
    return 'none';
  };

  const getGrowTier = () => {
    const status = lifecycleData?.subscriptions?.grow?.status || displayUser?.grow_subscription_status || 'none';
    // Status could be 'gold', 'silver', 'bronze', 'active', 'selected', or 'none'
    if (status === 'gold') return 'gold';
    if (status === 'silver') return 'silver';
    if (status === 'bronze' || status === 'active' || status === 'selected') return 'bronze';
    if (status !== 'none' && status !== 'locked') return 'bronze'; // Default active to bronze
    return 'none';
  };

  // Check if user has active subscription for Protect/Grow
  const hasProtectSubscription = () => {
    const tier = getProtectTier();
    return tier !== 'none';
  };

  const hasGrowSubscription = () => {
    const tier = getGrowTier();
    return tier !== 'none';
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <DashboardContent 
            user={displayUser} 
            tierColor={tierColor} 
            tierName={tierName} 
            dashboardData={dashboardData}
            userType={userType}
            lifecycle={lifecycle}
            starterPackTier={starterPackTier}
          />
        );
      case 'build':
        return (
          <BuildProgressModule 
            token={localStorage.getItem('hive_token')} 
            tierColor={tierColor} 
            starterPackTier={starterPackTier} 
            userType={userType}
            lifecycle={lifecycle}
          />
        );
      case 'protect':
        return (
          <ProtectCommandCenter 
            subscriptionStatus={hasProtectSubscription() ? 'active' : 'none'}
            subscriptionTier={getProtectTier()}
            tierColor="#C0C0C0"
            onActivate={openPlanSelection}
          />
        );
      case 'grow':
        return (
          <GrowCommandCenter 
            subscriptionStatus={hasGrowSubscription() ? 'active' : 'none'}
            subscriptionTier={getGrowTier()}
            tierColor="#D4AF37"
            onActivate={openPlanSelection}
            userId={displayUser?.user_id || displayUser?._id || displayUser?.id}
          />
        );
      case 'documents':
        return <DocumentsContent tierColor={tierColor} token={localStorage.getItem('hive_token')} />;
      case 'messages':
        return (
          <CarrierMessages 
            carrierId={displayUser?.user_id || displayUser?._id || displayUser?.id || 'carrier-001'}
            carrierName={displayUser?.company_name || displayUser?.full_name || 'Carrier'}
            tierColor={tierColor}
          />
        );
      case 'academy':
        // Redirect admins to the new Academy section in Command Hub
        if (userType === USER_TYPES.ADMIN) {
          navigate('/admin/academy');
          return null;
        }
        // Render Academy module for Academy students
        return (
          <AcademyModule 
            userId={displayUser?.user_id || displayUser?.id}
            userEmail={displayUser?.email}
          />
        );
      case 'finance':
        return userType === USER_TYPES.ADMIN ? <FinanceRedirect /> : null;
      default:
        return (
          <DashboardContent 
            user={displayUser} 
            tierColor={tierColor} 
            tierName={tierName}
            dashboardData={dashboardData}
            userType={userType}
            lifecycle={lifecycle}
            starterPackTier={starterPackTier}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="hive-dashboard-page">
      {/* Welcome Concierge — shown once on first login */}
      {!isAdmin && displayUser && (
        <WelcomeConcierge
          userId={displayUser.user_id || displayUser.id}
          firstName={displayUser.first_name || displayUser.full_name?.split(' ')?.[0] || ''}
        />
      )}

      {/* Milestone celebration overlay */}
      {currentMilestone && (
        <MilestoneCelebration
          milestone={currentMilestone}
          onClose={() => {
            const uid = displayUser?.user_id || displayUser?.id;
            if (uid && currentMilestone?.key) {
              axios.post(`${API}/hive/milestones/seen/${uid}/${currentMilestone.key}`).catch(() => {});
            }
            const [, ...rest] = milestoneQueue;
            setMilestoneQueue(rest);
            setCurrentMilestone(rest[0] || null);
          }}
        />
      )}

      <SEO 
        title={viewingAsCarrier ? `Admin View: ${impersonatedUser?.company_name}` : "The Hive Dashboard"}
        description="Your carrier portal dashboard"
        canonical="https://thehive.queenblogistics.com/hive/dashboard"
      />
      
      {/* Admin Viewing Mode Banner */}
      {viewingAsCarrier && (
        <div className="bg-amber-500 text-black py-3 px-6 flex items-center justify-between z-50" data-testid="admin-viewing-banner">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5" />
            <span className="font-bold">
              Admin Viewing Mode: {impersonatedUser?.company_name || 'Carrier'}
            </span>
            <span className="text-amber-900">
              ({impersonatedUser?.dispatch_tier?.toUpperCase()} | {impersonatedUser?.subscription_status})
            </span>
          </div>
          <button 
            onClick={handleExitImpersonation}
            className="flex items-center gap-2 bg-black text-white px-4 py-1.5 hover:bg-zinc-800 font-medium"
            data-testid="exit-impersonation-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Exit View Mode
          </button>
        </div>
      )}

      <div className="flex flex-1">
        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#121212] border border-white/10 text-white"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-72 bg-[#0A0A0A]/95 backdrop-blur-xl border-r border-white/5
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          flex flex-col
          ${viewingAsCarrier ? 'top-[52px]' : 'top-0'}
        `}>
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-6 border-b border-white/5">
            <img 
              src="/images/logos/honeycomb-logo.png" 
              alt="Queen B Logistics" 
              className="w-11 h-11"
              style={{ objectFit: 'contain' }}
            />
            <div>
              <div className="font-black text-white text-lg uppercase tracking-tight">
                {viewingAsCarrier ? 'Carrier View' : 'THE HIVE'}
              </div>
              <div className="text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] font-semibold">
                {viewingAsCarrier ? impersonatedUser?.company_name?.substring(0, 15) : 'Carrier Portal'}
              </div>
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 py-4 px-3 overflow-y-auto space-y-1">
            {/* Build Mode Banner - only show for build clients before completion */}
            {userType === USER_TYPES.BUILD && !lifecycle.buildComplete && (
              <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs" data-testid="build-mode-banner">
                <div className="flex items-center gap-2 mb-1">
                  <Layers className="w-4 h-4" />
                  <span className="font-bold uppercase tracking-wider">Build Mode Active</span>
                </div>
                <p className="text-amber-400/70">
                  {starterPackTier.name} Tier
                </p>
              </div>
            )}
            
            {/* Build Complete Banner */}
            {userType === USER_TYPES.BUILD && lifecycle.buildComplete && (
              <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 text-green-400 text-xs" data-testid="build-complete-banner">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-bold uppercase tracking-wider">Authority Active</span>
                </div>
                <p className="text-green-400/70">
                  Build Phase Complete
                </p>
              </div>
            )}
            
            {/* Grace Period Banner */}
            {lifecycle.gracePeriodActive && (
              <div className="mb-4 p-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs" data-testid="grace-period-banner">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="font-bold uppercase tracking-wider">Grace Period</span>
                </div>
                <p className="text-[#D4AF37]/80">
                  {lifecycle.gracePeriodDays} days to select your plan
                </p>
              </div>
            )}
            
            {/* Grace Period Expired Banner */}
            {lifecycle.gracePeriodExpired && !lifecycle.hasSelectedPlan && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-xs" data-testid="grace-expired-banner">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-bold uppercase tracking-wider">Select a Plan</span>
                </div>
                <p className="text-red-400/80">
                  Grace period ended
                </p>
              </div>
            )}
            
            {/* Navigation Items */}
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveSection(item.id);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200
                    ${isActive 
                      ? 'bg-[#D4AF37] text-black' 
                      : 'text-white/50 hover:bg-white/5 hover:text-white border-l-2 border-transparent hover:border-white/20'
                    }
                  `}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-black' : ''}`} style={{ color: isActive ? 'black' : tierColor }} />
                  <span className="uppercase tracking-wide text-sm font-semibold flex-1">
                    {item.label}
                  </span>
                  {item.badge && (
                    <span className={`text-[10px] px-2 py-0.5 font-bold ${
                      item.badge === 'COMPLETE' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : isActive 
                          ? 'bg-black/20 text-black' 
                          : 'bg-white/10 text-white/50 border border-white/10'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-black font-bold"
                style={{ backgroundColor: tierColor }}
              >
                {displayUser?.first_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {displayUser?.first_name} {displayUser?.last_name}
                </p>
                <p className="text-white/50 text-xs truncate">{displayUser?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-2 text-white/50 hover:text-white text-sm transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 flex flex-col min-h-screen overflow-hidden">
          {/* Top Bar */}
          <header className="backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-10 gap-2">
            <div className="flex items-center gap-2 sm:gap-4 ml-12 lg:ml-0 min-w-0 flex-1 overflow-hidden">
              {/* Tier Badge - Show Signature Program tier for Build clients, dispatch tier for operators */}
              {userType === USER_TYPES.BUILD ? (
                <div 
                  className="flex items-center gap-2 px-3 sm:px-4 py-1.5 flex-shrink-0"
                  style={{ backgroundColor: `${starterPackTier.name === 'Elite' ? '#D4AF37' : starterPackTier.name === 'Accelerator' ? '#C0C0C0' : '#CD7F32'}15`, border: `1px solid ${starterPackTier.name === 'Elite' ? '#D4AF37' : starterPackTier.name === 'Accelerator' ? '#C0C0C0' : '#CD7F32'}30` }}
                  data-testid="tier-badge"
                >
                  <Crown className="w-4 h-4 flex-shrink-0" style={{ color: starterPackTier.name === 'Elite' ? '#D4AF37' : starterPackTier.name === 'Accelerator' ? '#C0C0C0' : '#CD7F32' }} />
                  <span style={{ color: starterPackTier.name === 'Elite' ? '#D4AF37' : starterPackTier.name === 'Accelerator' ? '#C0C0C0' : '#CD7F32' }} className="text-xs sm:text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                    {starterPackTier.name}<span className="hidden sm:inline"> Package</span>
                  </span>
                </div>
              ) : (
                <div 
                  className="flex items-center gap-2 px-3 sm:px-4 py-1.5 flex-shrink-0"
                  style={{ backgroundColor: `${tierColor}15`, border: `1px solid ${tierColor}30` }}
                  data-testid="tier-badge"
                >
                  <Crown className="w-4 h-4 flex-shrink-0" style={{ color: tierColor }} />
                  <span style={{ color: tierColor }} className="text-xs sm:text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                    {tierName}<span className="hidden sm:inline"> Member</span>
                  </span>
                </div>
              )}
              
              {/* User Type Badge */}
              {userType === USER_TYPES.BUILD && (
                <div className="hidden sm:flex items-center gap-2 px-4 py-1.5 bg-purple-500/10 border border-purple-500/30 flex-shrink-0">
                  <Layers className="w-4 h-4 text-purple-400" />
                  <span className="text-purple-400 text-sm font-bold uppercase tracking-wider">Build Client</span>
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              {!viewingAsCarrier && !isAdmin && (
                <button
                  onClick={openPlanSelection}
                  className="flex items-center gap-2 text-white/40 hover:text-[#D4AF37] transition-all duration-200"
                  data-testid="manage-subscription-button"
                >
                  <CreditCard className="w-4 h-4" />
                  <span className="hidden sm:inline text-sm font-semibold">Subscription</span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-white/40 hover:text-red-400 transition-all duration-200"
                data-testid="header-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline text-sm font-semibold">Sign Out</span>
              </button>
            </div>
          </header>

          {/* Error Banner */}
          {error && (
            <div className="bg-red-500/10 border-b border-red-500/30 px-6 py-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-red-500 text-sm">{error}</span>
            </div>
          )}

          {/* Page Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {renderContent()}
          </div>

          {/* Footer */}
          <footer className="border-t border-white/5 px-4 sm:px-6 py-4">
            <p className="text-white/20 text-[10px] sm:text-xs text-center uppercase tracking-[0.15em] sm:tracking-[0.2em]">
              Queen B Logistics • The Hive Network
            </p>
          </footer>
        </main>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && !viewingAsCarrier && (
        <SubscriptionModal
          subscriptionData={subscriptionData}
          subscriptionLoading={subscriptionLoading}
          actionLoading={actionLoading}
          setActionLoading={setActionLoading}
          onClose={() => setShowSubscriptionModal(false)}
          onRefresh={fetchSubscriptionData}
          navigate={navigate}
        />
      )}
    </div>
  );
};

// ============ ADMIN COMMAND DASHBOARD ============
const AdminCommandDashboard = ({ 
  user, 
  adminData, 
  carrierList, 
  searchQuery, 
  setSearchQuery, 
  onViewAsCarrier, 
  onLogout,
  onRefresh 
}) => {
  const navigate = useNavigate();
  const [activeDetailPanel, setActiveDetailPanel] = useState(null);
  const stats = adminData?.stats || {};
  const tierBreakdown = adminData?.tier_breakdown || {};
  const revenue = adminData?.revenue_snapshot || {};

  const handleMetricClick = (metricKey) => {
    setActiveDetailPanel(metricKey);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-command-dashboard">
      <SEO title="Admin Command Dashboard - The Hive" />
      
      {/* Detail Panel */}
      {activeDetailPanel && (
        <AdminDetailPanel
          metricKey={activeDetailPanel}
          onClose={() => setActiveDetailPanel(null)}
          onViewCarrier={onViewAsCarrier}
          onRefreshDashboard={onRefresh}
        />
      )}
      
      {/* Header */}
      <header className="bg-[#0D0D0D] border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <img 
              src="/images/logos/honeycomb-logo.png" 
              alt="Queen B Logistics" 
              className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0"
              style={{ objectFit: 'contain' }}
            />
            <div className="min-w-0">
              <div className="font-['Barlow_Condensed'] font-bold text-white text-sm sm:text-lg uppercase tracking-wide">Admin</div>
              <div className="text-[#D4AF37] text-[10px] sm:text-xs uppercase tracking-wider truncate">Queen B Logistics</div>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-4 flex-shrink-0 overflow-x-auto">
            <button
              onClick={() => navigate('/admin/starter-pack')}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-purple-500/10 border border-purple-500/30 text-purple-400 hover:bg-purple-500/20 transition-colors flex-shrink-0"
              data-testid="admin-build-btn"
            >
              <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">Build</span>
            </button>
            <button
              onClick={() => navigate('/admin/protect')}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-[#C0C0C0]/10 border border-[#C0C0C0]/30 text-[#C0C0C0] hover:bg-[#C0C0C0]/20 transition-colors flex-shrink-0"
              data-testid="admin-protect-btn"
            >
              <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">Protect</span>
            </button>
            <button
              onClick={() => navigate('/admin/grow')}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-colors flex-shrink-0"
              data-testid="admin-grow-btn"
            >
              <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">Grow</span>
            </button>
            <button
              onClick={() => navigate('/admin/finance')}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors flex-shrink-0"
              data-testid="admin-finance-btn"
            >
              <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">Finance</span>
            </button>
            <button
              onClick={() => navigate('/admin/analytics')}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20 transition-colors flex-shrink-0"
              data-testid="admin-analytics-btn"
            >
              <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">Analytics</span>
            </button>
            <button
              onClick={onRefresh}
              className="p-1.5 sm:p-2 text-white/60 hover:text-[#D4AF37] transition-colors flex-shrink-0"
              title="Refresh"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <div className="hidden md:flex items-center gap-2 text-white/60 flex-shrink-0">
              <Crown className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-sm truncate max-w-[150px]">{user?.email}</span>
            </div>
            <button
              onClick={onLogout}
              className="p-1.5 sm:p-2 text-white/60 hover:text-white transition-colors flex-shrink-0"
              data-testid="admin-logout-btn"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Welcome Section */}
        <div className="mb-4 sm:mb-8">
          <h1 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-bold text-white uppercase mb-2">
            Admin <span className="text-[#D4AF37]">Command Center</span>
          </h1>
          <p className="text-white/50 text-sm sm:text-base">Click any metric card to view details and take actions</p>
        </div>

        {/* Interactive Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <InteractiveStatCard 
            label="Total Carriers" 
            value={stats.total_carriers || 0} 
            icon={Users} 
            color="#3B82F6" 
            metricKey="total_carriers"
            onClick={handleMetricClick}
          />
          <InteractiveStatCard 
            label="Active" 
            value={stats.active_carriers || 0} 
            icon={CheckCircle} 
            color="#10B981" 
            metricKey="active_carriers"
            onClick={handleMetricClick}
          />
          <InteractiveStatCard 
            label="Trial Users" 
            value={stats.trial_users || 0} 
            icon={Clock} 
            color="#F59E0B" 
            metricKey="trial_users"
            onClick={handleMetricClick}
          />
          <InteractiveStatCard 
            label="Pending Onboard" 
            value={stats.pending_onboarding || 0} 
            icon={Send} 
            color="#8B5CF6" 
            metricKey="pending_onboarding"
            onClick={handleMetricClick}
          />
          <InteractiveStatCard 
            label="Applications" 
            value={stats.pending_applications || 0} 
            icon={FileText} 
            color="#EC4899" 
            metricKey="pending_applications"
            onClick={handleMetricClick}
          />
          <InteractiveStatCard 
            label="Payment Issues" 
            value={stats.payment_failures || 0} 
            icon={XCircle} 
            color="#EF4444" 
            metricKey="payment_failures"
            onClick={handleMetricClick}
          />
        </div>

        {/* Tier Breakdown & Revenue */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-8">
          {/* Tier Breakdown */}
          <div className="bg-[#121212] border border-white/5 p-4 sm:p-6">
            <h3 className="font-['Barlow_Condensed'] text-base sm:text-lg font-bold text-[#D4AF37] uppercase mb-3 sm:mb-4 flex items-center gap-2">
              <Crown className="w-4 h-4 sm:w-5 sm:h-5" />
              Tier Breakdown
            </h3>
            <div className="space-y-3 sm:space-y-4">
              {[
                { tier: 'Gold', count: tierBreakdown.gold || 0, color: TIER_COLORS.gold },
                { tier: 'Silver', count: tierBreakdown.silver || 0, color: TIER_COLORS.silver },
                { tier: 'Bronze', count: tierBreakdown.bronze || 0, color: TIER_COLORS.bronze },
              ].map((t) => (
                <div key={t.tier} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-3 h-3 sm:w-4 sm:h-4 rounded" style={{ backgroundColor: t.color }} />
                    <span className="text-white text-sm sm:text-base">{t.tier}</span>
                  </div>
                  <span className="text-white font-bold">{t.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Revenue Snapshot */}
          <div className="bg-[#121212] border border-white/5 p-4 sm:p-6">
            <h3 className="font-['Barlow_Condensed'] text-base sm:text-lg font-bold text-[#D4AF37] uppercase mb-3 sm:mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
              Revenue Snapshot
            </h3>
            <div className="space-y-3 sm:space-y-4">
              <div>
                <p className="text-white/50 text-xs sm:text-sm">Est. MRR</p>
                <p className="text-2xl sm:text-3xl font-bold text-green-400">${revenue.estimated_mrr?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div>
                  <p className="text-white/40">Active Subs</p>
                  <p className="text-white font-medium">{revenue.active_subscriptions || 0}</p>
                </div>
                <div>
                  <p className="text-white/40">Pending Conv.</p>
                  <p className="text-amber-400 font-medium">{revenue.trial_conversions_pending || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Compliance Alerts */}
          <div className="bg-[#121212] border border-white/5 p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
            <h3 className="font-['Barlow_Condensed'] text-base sm:text-lg font-bold text-[#D4AF37] uppercase mb-3 sm:mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
              Alerts
            </h3>
            <div className="space-y-2 sm:space-y-3 max-h-48 overflow-y-auto">
              {adminData?.compliance_alerts?.length > 0 ? (
                adminData.compliance_alerts.slice(0, 5).map((alert, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-red-500/10 border border-red-500/20">
                    <span className="text-white text-xs sm:text-sm truncate flex-1">{alert.company_name}</span>
                    <span className="text-red-400 text-xs sm:text-sm font-medium">{alert.health_score}%</span>
                  </div>
                ))
              ) : (
                <p className="text-white/40 text-xs sm:text-sm">No compliance alerts</p>
              )}
              {adminData?.expiring_documents?.length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-amber-400 text-[10px] sm:text-xs uppercase mb-2">Expiring Docs</p>
                  {adminData.expiring_documents.slice(0, 3).map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs sm:text-sm py-1">
                      <span className="text-white/70 truncate">{doc.company_name}</span>
                      <span className="text-amber-400">{doc.days_until_expiry}d</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Carrier Table */}
        <div className="bg-[#121212] border border-white/5">
          <div className="p-3 sm:p-4 border-b border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <h3 className="font-['Barlow_Condensed'] text-base sm:text-lg font-bold text-[#D4AF37] uppercase flex items-center gap-2">
              <Truck className="w-4 h-4 sm:w-5 sm:h-5" />
              Carrier Management
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input
                type="text"
                placeholder="Search carriers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 text-white pl-10 pr-4 py-2 text-sm w-full sm:w-64 focus:outline-none focus:border-[#D4AF37]"
                data-testid="carrier-search-input"
              />
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead className="bg-[#0A0A0A]">
                <tr className="text-left text-white/50 text-[10px] sm:text-xs uppercase tracking-wider">
                  <th className="p-3 sm:p-4">Carrier</th>
                  <th className="p-3 sm:p-4">Tier</th>
                  <th className="p-3 sm:p-4">Compliance</th>
                  <th className="p-3 sm:p-4">Status</th>
                  <th className="p-3 sm:p-4">Billing</th>
                  <th className="p-3 sm:p-4">Authority</th>
                  <th className="p-3 sm:p-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {carrierList.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 sm:p-8 text-center text-white/40 text-sm">
                      No carriers found
                    </td>
                  </tr>
                ) : (
                  carrierList.map((carrier) => (
                    <tr key={carrier.id} className="hover:bg-white/5" data-testid={`carrier-row-${carrier.id}`}>
                      <td className="p-3 sm:p-4">
                        <div>
                          <p className="text-white font-medium text-sm sm:text-base">{carrier.company_name}</p>
                          <p className="text-white/50 text-xs sm:text-sm">{carrier.contact_name}</p>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4">
                        <span 
                          className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium uppercase"
                          style={{ 
                            backgroundColor: `${TIER_COLORS[carrier.dispatch_tier]}20`,
                            color: TIER_COLORS[carrier.dispatch_tier]
                          }}
                        >
                          {carrier.dispatch_tier}
                        </span>
                      </td>
                      <td className="p-3 sm:p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-12 sm:w-16 h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full"
                              style={{
                                width: `${carrier.compliance_health_score}%`,
                                backgroundColor: carrier.compliance_health_score >= 75 ? '#10B981' :
                                  carrier.compliance_health_score >= 50 ? '#F59E0B' : '#EF4444'
                              }}
                            />
                          </div>
                          <span className="text-white text-sm">{carrier.compliance_health_score}%</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-medium ${
                          carrier.subscription_status === 'active' ? 'bg-green-500/10 text-green-400' :
                          carrier.subscription_status === 'trial' ? 'bg-amber-500/10 text-amber-400' :
                          'bg-zinc-500/10 text-zinc-400'
                        }`}>
                          {carrier.subscription_status || 'Pending'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-medium ${
                          carrier.billing_status === 'active' ? 'bg-green-500/10 text-green-400' :
                          carrier.billing_status === 'trial' ? 'bg-amber-500/10 text-amber-400' :
                          'bg-zinc-500/10 text-zinc-400'
                        }`}>
                          {carrier.billing_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 text-xs font-medium bg-green-500/10 text-green-400">
                          {carrier.authority_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {carrier.has_hive_account && (
                            <button
                              onClick={() => onViewAsCarrier(carrier.id)}
                              className="p-2 text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                              title="View as Carrier"
                              data-testid={`impersonate-${carrier.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

// ============ CONTENT COMPONENTS ============

const SAMPLE_LOADS = [
  { id: 'LD-2024-001', origin: 'Dallas, TX', destination: 'Atlanta, GA', status: 'In Transit', rate: 2850, miles: 781, pickup: '12/15/2024', delivery: '12/17/2024' },
  { id: 'LD-2024-002', origin: 'Phoenix, AZ', destination: 'Denver, CO', status: 'Delivered', rate: 1950, miles: 602, pickup: '12/12/2024', delivery: '12/14/2024' },
  { id: 'LD-2024-003', origin: 'Chicago, IL', destination: 'Miami, FL', status: 'Pending', rate: 3200, miles: 1381, pickup: '12/18/2024', delivery: '12/21/2024' },
  { id: 'LD-2024-004', origin: 'Los Angeles, CA', destination: 'Seattle, WA', status: 'In Transit', rate: 2100, miles: 1135, pickup: '12/14/2024', delivery: '12/16/2024' },
];

const SAMPLE_MESSAGES = [
  { id: 1, from: 'Sarah - Dispatch', subject: 'Load LD-2024-001 Update', preview: 'Hi! Just wanted to confirm the delivery window...', time: '2 hours ago', unread: true },
  { id: 2, from: 'Queen B Logistics', subject: 'Weekly Settlement Ready', preview: 'Your settlement for the week ending 12/14 is now available...', time: '1 day ago', unread: true },
  { id: 3, from: 'Marcus - Operations', subject: 'Re: Rate Confirmation', preview: 'The rate has been confirmed at $2.85/mile...', time: '2 days ago', unread: false },
];

// Dashboard Content Component
const DashboardContent = ({ user, tierColor, tierName, dashboardData, userType, lifecycle, starterPackTier }) => {
  const [activePanel, setActivePanel] = useState(null);
  
  const panelConfigs = {
    loads: {
      title: 'Active Loads',
      icon: Truck,
      content: (
        <div className="space-y-3">
          {SAMPLE_LOADS.map((load) => (
            <div key={load.id} className="bg-[#0A0A0A] p-4 border border-white/5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#D4AF37] font-medium">{load.id}</span>
                <span className={`px-2 py-1 text-xs font-medium ${
                  load.status === 'In Transit' ? 'bg-blue-500/20 text-blue-400' :
                  load.status === 'Delivered' ? 'bg-green-500/20 text-green-400' :
                  'bg-amber-500/20 text-amber-400'
                }`}>
                  {load.status}
                </span>
              </div>
              <p className="text-white">{load.origin} → {load.destination}</p>
              <div className="flex justify-between mt-2 text-sm text-white/50">
                <span>{load.miles} mi</span>
                <span className="text-green-400 font-medium">${load.rate.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )
    },
    messages: {
      title: 'Messages',
      icon: MessageSquare,
      content: (
        <div className="space-y-3">
          {SAMPLE_MESSAGES.map((msg) => (
            <div key={msg.id} className={`bg-[#0A0A0A] p-4 border border-white/5 ${msg.unread ? 'border-l-2 border-l-[#D4AF37]' : ''}`}>
              <div className="flex items-start justify-between mb-2">
                <p className="text-white font-medium">{msg.subject}</p>
                {msg.unread && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
              </div>
              <p className="text-white/50 text-sm">{msg.from}</p>
              <p className="text-white/40 text-xs mt-1">{msg.time}</p>
            </div>
          ))}
        </div>
      )
    },
    documents: {
      title: 'Document Vault',
      icon: FileText,
      content: <DocumentsContent token={localStorage.getItem('hive_token')} />
    },
    reports: {
      title: 'Performance Reports',
      icon: BarChart3,
      content: (
        <div className="space-y-4">
          <div className="bg-[#0A0A0A] p-4 border border-white/5">
            <h4 className="text-white font-medium mb-3">Weekly Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-white/40">Total Revenue</span>
                <p className="text-[#D4AF37] font-bold text-lg">$8,420</p>
              </div>
              <div>
                <span className="text-white/40">Miles Driven</span>
                <p className="text-white font-bold text-lg">2,847</p>
              </div>
              <div>
                <span className="text-white/40">Avg RPM</span>
                <p className="text-green-400 font-bold text-lg">$3.24</p>
              </div>
              <div>
                <span className="text-white/40">Deadhead %</span>
                <p className="text-white font-bold text-lg">12%</p>
              </div>
            </div>
          </div>
          <div className="bg-[#0A0A0A] p-4 border border-white/5">
            <h4 className="text-white font-medium mb-3">Recent Activity</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/50">Loads Completed</span>
                <span className="text-white">12</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">On-Time Delivery</span>
                <span className="text-green-400">98%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/50">Customer Rating</span>
                <span className="text-[#D4AF37]">4.9/5</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    academy: {
      title: 'Queen B Academy',
      icon: GraduationCap,
      content: <AcademyModule userId={user?.user_id || user?._id || user?.id} userEmail={user?.email} />
    },
    resources: {
      title: 'My Resources',
      icon: FolderOpen,
      content: <ResourcesModule userEmail={user?.email} />
    }
  };
  
  return (
    <div data-testid="carrier-dashboard-content">
      <h1 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-bold text-white uppercase mb-2">
        Welcome back, <span style={{ color: tierColor }}>{user?.first_name || 'Dashboard'}</span>
      </h1>
      <p className="text-white/50 mb-6 sm:mb-8 text-sm sm:text-base">
        {user?.company_name} • {userType === USER_TYPES.BUILD ? `${starterPackTier.name} Package` : `${tierName} Member`}
      </p>

      {/* Onboarding Progress Map — shows stage through first 30 days */}
      <OnboardingProgressMap currentStage={user?.documents_completed ? 'active' : 'signed'} />

      {/* Referral Widget — encourages organic growth */}
      {user?.documents_completed && (
        <div className="mb-6">
          <ReferralWidget userId={user?.user_id || user?.id} />
        </div>
      )}

      {/* User Type Specific Content */}
      {userType === USER_TYPES.BUILD && !lifecycle?.buildComplete && (
        <div className="bg-purple-500/10 border border-purple-500/30 p-4 mb-6" data-testid="build-status-card">
          <div className="flex items-center gap-3 mb-2">
            <Layers className="w-6 h-6 text-purple-400" />
            <h3 className="text-purple-400 font-bold uppercase">Build Phase Active</h3>
          </div>
          <p className="text-white/60 text-sm">
            Complete your authority setup in the Build tab to unlock Protect and Grow services.
          </p>
        </div>
      )}

      {/* Authority Service Timers - Show for Build clients with active authority */}
      {userType === USER_TYPES.BUILD && (
        <div className="mb-6">
          <AuthorityStatusWidget />
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard 
          label="Active Loads" 
          value="4" 
          icon={Truck} 
          color={tierColor} 
          onClick={() => setActivePanel('loads')}
          clickable
        />
        <StatCard label="Messages" value="2" icon={MessageSquare} color="#3B82F6" onClick={() => setActivePanel('messages')} clickable />
        <StatCard label="Documents" value="5" icon={FileText} color="#10B981" />
        <StatCard label="Compliance" value="100%" icon={Shield} color="#C0C0C0" />
      </div>

      {/* Quick Actions */}
      <div className="bg-[#121212] border border-white/5 p-4 sm:p-6">
        <h2 className="font-['Barlow_Condensed'] text-lg sm:text-xl font-bold text-white uppercase mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 sm:gap-4">
          <QuickAction icon={Truck} label="View Loads" onClick={() => setActivePanel('loads')} />
          <QuickAction icon={FileText} label="Upload Doc" onClick={() => setActivePanel('documents')} />
          <QuickAction icon={MessageSquare} label="New Message" onClick={() => setActivePanel('messages')} />
          <QuickAction icon={BarChart3} label="View Reports" onClick={() => setActivePanel('reports')} />
          <QuickAction icon={GraduationCap} label="Academy" onClick={() => setActivePanel('academy')} />
          <QuickAction icon={FolderOpen} label="Resources" onClick={() => setActivePanel('resources')} />
        </div>
      </div>

      {/* Slide-out Panel */}
      {activePanel && panelConfigs[activePanel] && (
        <CarrierSlideOutPanel
          isOpen={!!activePanel}
          onClose={() => setActivePanel(null)}
          title={panelConfigs[activePanel].title}
          icon={panelConfigs[activePanel].icon}
        >
          {panelConfigs[activePanel].content}
        </CarrierSlideOutPanel>
      )}
    </div>
  );
};

// Carrier Slide-out Panel Component
const CarrierSlideOutPanel = ({ isOpen, onClose, title, icon: Icon, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50" data-testid="carrier-slideout-panel">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0D0D0D] border-l border-white/10 shadow-2xl overflow-hidden flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
          <h2 className="font-['Barlow_Condensed'] text-lg sm:text-xl font-bold text-white uppercase flex items-center gap-2">
            <Icon className="w-5 h-5 text-[#D4AF37]" />
            {title}
          </h2>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white hover:bg-white/10 transition-colors rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
      </div>
      <style>{`
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};

// Protect Content (for users with active Protect subscription)
// Documents Content
const DocumentsContent = ({ tierColor, token }) => {
  const [documents, setDocuments] = useState([]);
  const [signedAgreements, setSignedAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signedLoading, setSignedLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [viewingSigned, setViewingSigned] = useState(null);

  const CATEGORY_LABELS = {
    global: { label: 'Required for All', color: '#D4AF37' },
    build: { label: 'Build', color: '#F59E0B' },
    protect: { label: 'Protect', color: '#3B82F6' },
    grow: { label: 'Grow', color: '#10B981' }
  };
  
  const DOC_TYPES = [
    'Certificate of Insurance (COI)',
    'W-9 Form',
    'Carrier Agreement',
    'MC Authority',
    'BOC-3 Filing',
    "Driver's License",
    'Medical Card',
    'Vehicle Registration',
    'Other'
  ];

  useEffect(() => {
    fetchDocuments();
    fetchSignedAgreements();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data.documents || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignedAgreements = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('hive_user') || '{}');
      const userId = userData.user_id || userData.id || userData._id;
      if (!userId) {
        setSignedLoading(false);
        return;
      }
      const res = await axios.get(`${API}/documents/admin/user/${userId}`);
      setSignedAgreements(res.data || []);
    } catch (err) {
      console.error('Failed to fetch signed agreements:', err);
    } finally {
      setSignedLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedType) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', selectedType);
    
    try {
      await axios.post(`${API}/hive/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchDocuments();
      setSelectedType('');
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="documents-content">
      <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-2">Document Vault</h1>
      <p className="text-white/50 mb-6">Your signed agreements and uploaded carrier documents</p>

      {/* Signed Onboarding Agreements */}
      <div className="bg-[#121212] border border-white/5 mb-6" data-testid="signed-agreements-section">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-[#D4AF37] uppercase">
              Signed Agreements ({signedAgreements.length})
            </h2>
          </div>
          <span className="text-xs text-white/40 uppercase tracking-wider">Onboarding Documents</span>
        </div>
        {signedLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto" />
          </div>
        ) : signedAgreements.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm">
            No signed agreements yet. Complete onboarding to see your documents here.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {signedAgreements.map((doc) => {
              const catInfo = CATEGORY_LABELS[doc.category] || { label: doc.category, color: '#D4AF37' };
              return (
                <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-white/5">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <FileText className="w-5 h-5 flex-shrink-0" style={{ color: catInfo.color }} />
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">{doc.title}</p>
                      <p className="text-white/50 text-sm">
                        Signed {new Date(doc.signed_at).toLocaleDateString()} • {doc.printed_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                    <span
                      className="px-2 py-1 text-xs font-medium uppercase tracking-wider"
                      style={{ backgroundColor: `${catInfo.color}20`, color: catInfo.color }}
                    >
                      {catInfo.label}
                    </span>
                    <button
                      onClick={() => setViewingSigned(doc)}
                      className="p-2 hover:bg-white/10 transition-colors"
                      title="View Document"
                      data-testid={`view-signed-${doc.document_id}`}
                    >
                      <Eye className="w-4 h-4 text-white/60" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div className="bg-[#121212] border border-white/5 p-6 mb-6">
        <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-[#D4AF37] uppercase mb-4">Upload Document</h2>
        <div className="flex flex-col sm:flex-row gap-4">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="flex-1 bg-[#0A0A0A] border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-[#D4AF37]"
            data-testid="doc-type-select"
          >
            <option value="">Select document type...</option>
            {DOC_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <label className={`flex items-center gap-2 px-6 py-3 font-medium cursor-pointer transition-colors ${
            selectedType 
              ? 'bg-[#D4AF37] text-black hover:bg-[#B8962E]' 
              : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
          }`}>
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            {uploading ? 'Uploading...' : 'Upload File'}
            <input
              type="file"
              className="hidden"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              onChange={handleUpload}
              disabled={!selectedType || uploading}
              data-testid="file-upload-input"
            />
          </label>
        </div>
        <p className="text-white/40 text-xs mt-2">Supported: PDF, PNG, JPG, DOC, DOCX (max 10MB)</p>
      </div>
      
      {/* Documents List */}
      <div className="bg-[#121212] border border-white/5">
        <div className="p-4 border-b border-white/5">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-[#D4AF37] uppercase">
            Your Documents ({documents.length})
          </h2>
        </div>
        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto" />
          </div>
        ) : documents.length === 0 ? (
          <div className="p-8 text-center text-white/40">
            No documents uploaded yet
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {documents.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-4 hover:bg-white/5">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-white font-medium">{doc.name}</p>
                    <p className="text-white/50 text-sm">{doc.doc_type} • {doc.upload_date}</p>
                  </div>
                </div>
                <span className="px-2 py-1 text-xs font-medium bg-green-500/20 text-green-400">
                  {doc.status || 'Valid'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Signed Agreement Viewer Modal */}
      {viewingSigned && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setViewingSigned(null)}>
          <div className="bg-[#0D0D0D] border border-white/10 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">{viewingSigned.title}</h2>
                <p className="text-white/50 text-sm">
                  Signed {new Date(viewingSigned.signed_at).toLocaleDateString()} at {new Date(viewingSigned.signed_at).toLocaleTimeString()}
                </p>
              </div>
              <button onClick={() => setViewingSigned(null)} className="p-2 hover:bg-white/10 transition-colors">
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 p-3">
                  <p className="text-white/50 text-xs uppercase mb-1">Signed By</p>
                  <p className="text-white">{viewingSigned.printed_name}</p>
                </div>
                <div className="bg-white/5 p-3">
                  <p className="text-white/50 text-xs uppercase mb-1">Category</p>
                  <p className="text-white capitalize">{viewingSigned.category}</p>
                </div>
                <div className="bg-white/5 p-3 col-span-2">
                  <p className="text-white/50 text-xs uppercase mb-1">IP Address (audit)</p>
                  <p className="text-white font-mono text-sm">{viewingSigned.ip_address}</p>
                </div>
              </div>
              <div className="border border-[#D4AF37]/30 bg-black/40 p-6 text-center">
                <p className="text-white/50 text-xs uppercase mb-2 tracking-wider">E-Signature</p>
                <p className="text-[#D4AF37] text-3xl" style={{ fontFamily: "'Dancing Script', cursive" }}>
                  {viewingSigned.printed_name}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const MessagesContent = ({ tierColor }) => {
  return (
    <div data-testid="messages-content">
      <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-2">Messages</h1>
      <p className="text-white/50 mb-6">Communication with Queen B Logistics team</p>
      
      <div className="bg-[#121212] border border-white/5">
        <div className="p-4 border-b border-white/5 flex items-center justify-between">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold text-[#D4AF37] uppercase">Inbox</h2>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black font-medium text-sm">
            <Send className="w-4 h-4" />
            New Message
          </button>
        </div>
        <div className="divide-y divide-white/5">
          {SAMPLE_MESSAGES.map((msg) => (
            <div key={msg.id} className={`p-4 hover:bg-white/5 cursor-pointer ${msg.unread ? 'border-l-2 border-l-[#D4AF37]' : ''}`}>
              <div className="flex items-start justify-between mb-2">
                <p className="text-white font-medium">{msg.subject}</p>
                {msg.unread && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
              </div>
              <p className="text-white/70 text-sm mb-1">{msg.preview}</p>
              <div className="flex items-center justify-between">
                <p className="text-white/50 text-xs">{msg.from}</p>
                <p className="text-white/40 text-xs">{msg.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Finance Redirect (Admin only)
const FinanceRedirect = () => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/admin/finance');
  }, [navigate]);
  return null;
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color, onClick, clickable }) => (
  <div 
    className={`bg-[#121212] border border-white/5 p-4 sm:p-6 ${clickable ? 'cursor-pointer hover:border-white/20 transition-colors' : ''}`}
    onClick={clickable ? onClick : undefined}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-white/50 text-xs sm:text-sm uppercase">{label}</span>
      <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
    </div>
    <p className="text-xl sm:text-2xl font-bold text-white">{value}</p>
  </div>
);

// Quick Action Button
const QuickAction = ({ icon: Icon, label, onClick }) => (
  <button 
    onClick={onClick}
    className="flex flex-col items-center gap-2 p-4 bg-[#0A0A0A] border border-white/5 hover:border-[#D4AF37]/50 transition-colors"
  >
    <Icon className="w-5 h-5 text-[#D4AF37]" />
    <span className="text-white/70 text-xs sm:text-sm">{label}</span>
  </button>
);

// Subscription Modal
const SubscriptionModal = ({ subscriptionData, subscriptionLoading, actionLoading, setActionLoading, onClose, onRefresh, navigate }) => {
  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('hive_token');
      await axios.post(`${API}/hive/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Subscription cancelled. You will retain access until the end of your billing period.');
      onRefresh();
    } catch (err) {
      alert('Failed to cancel subscription. Please contact support.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="subscription-modal">
      <div className="bg-[#121212] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#D4AF37]" />
            Manage Subscription
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          {subscriptionLoading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white/60">Loading...</p>
            </div>
          ) : subscriptionData ? (
            <div className="space-y-6">
              <div className="bg-[#0A0A0A] border border-white/5 p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white/50 text-sm">Status</span>
                  <span className={`px-3 py-1 text-sm font-medium ${
                    subscriptionData.subscription_status === 'active' 
                      ? 'bg-green-500/20 text-green-500 border border-green-500/30'
                      : subscriptionData.subscription_status === 'trial'
                      ? 'bg-amber-500/20 text-amber-500 border border-amber-500/30'
                      : subscriptionData.subscription_status === 'build'
                      ? 'bg-[#CD7F32]/20 text-[#CD7F32] border border-[#CD7F32]/30'
                      : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                  }`}>
                    {subscriptionData.subscription_status === 'trial' ? 'Trial' :
                     subscriptionData.subscription_status === 'active' ? 'Active' :
                     subscriptionData.subscription_status === 'build' ? 'Build Phase' : 
                     subscriptionData.subscription_status?.charAt(0).toUpperCase() + subscriptionData.subscription_status?.slice(1) || 'Unknown'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-white/40 block">Tier</span>
                    <span className="text-white capitalize">{subscriptionData.dispatch_tier || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-white/40 block">Plan</span>
                    <span className="text-white capitalize">{subscriptionData.subscription_plan || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {subscriptionData.can_cancel && (
                <button 
                  onClick={handleCancelSubscription} 
                  disabled={actionLoading} 
                  className="w-full py-3 bg-red-500/10 border border-red-500/30 text-red-500 font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Cancel Subscription'}
                </button>
              )}
            </div>
          ) : (
            <p className="text-center py-8 text-white/60">Failed to load subscription data.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default HiveDashboardPage;
